/* ==========================================================================
   Product Collection Motion Pro
   File: assets/product-collection-motion-pro-core.js
   ========================================================================== */

(() => {
  'use strict';

  /* ==========================================================================
     01. GLOBAL CONSTANTS / REGISTRY
     ========================================================================== */

  const ROOT_SELECTOR = '[data-pcmp-root]';
  const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
  const SECTION_EVENT_NAMESPACE = 'pcmp';
  const REGISTRY = new Map();

  const MODULE_KEYS = {
    slider: 'PCMPProductSlider',
    cardMedia: 'PCMPCardMediaManager',
  };


  /* ==========================================================================
     02. SAFE HELPERS
     ========================================================================== */

  const safeCall = (fn, ...args) => {
    try {
      if (typeof fn === 'function') {
        return fn(...args);
      }
    } catch (_) {}
    return undefined;
  };

  const parseBoolean = (value, fallback = false) => {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  };

  const parseNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseString = (value, fallback = '') => {
    if (value == null) return fallback;
    const text = String(value).trim();
    return text === '' ? fallback : text;
  };

  const getAttr = (element, name, fallback = '') => {
    if (!element) return fallback;
    const value = element.getAttribute(name);
    return value == null ? fallback : value;
  };

  const matchesDesktop = () => {
    return !!(window.matchMedia && window.matchMedia(DESKTOP_MEDIA_QUERY).matches);
  };

  const prefersReducedMotion = () => {
    return !!(
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  };

  const dispatchSectionEvent = (root, name, detail = {}) => {
    if (!root) return;
    root.dispatchEvent(
      new CustomEvent(`${SECTION_EVENT_NAMESPACE}:${name}`, {
        bubbles: true,
        detail,
      })
    );
  };

  const rafThrottle = (callback) => {
    let rafId = 0;

    return (...args) => {
      if (rafId) window.cancelAnimationFrame(rafId);

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        callback(...args);
      });
    };
  };

  const resolveModule = (moduleName) => {
    const key = MODULE_KEYS[moduleName];
    if (!key) return null;

    const candidate = window[key];
    return typeof candidate === 'function' ? candidate : null;
  };


  /* ==========================================================================
     03. SECTION CONTEXT BUILDER
     ========================================================================== */

  const buildSectionContext = (root) => {
    const slider = root.querySelector('[data-pcmp-slider]');
    const viewport = root.querySelector('[data-pcmp-viewport]');
    const track = root.querySelector('[data-pcmp-track]');
    const controls = root.querySelector('[data-pcmp-controls]');
    const prevButton = root.querySelector('[data-pcmp-prev]');
    const nextButton = root.querySelector('[data-pcmp-next]');
    const slides = Array.from(root.querySelectorAll('[data-pcmp-slide]'));
    const cards = Array.from(root.querySelectorAll('[data-pcmp-card]'));
    const mediaImages = Array.from(root.querySelectorAll('[data-pcmp-media-image]'));

    const sectionId = parseString(getAttr(root, 'data-pcmp-section-id'));
    const anchorId = parseString(getAttr(root, 'data-pcmp-anchor-id'));
    const hasProducts = parseBoolean(getAttr(root, 'data-pcmp-has-products'), false);

    const settings = {
      hasProducts,
      productsLimit: parseNumber(getAttr(root, 'data-pcmp-products-limit'), 8),

      desktopColumns: parseNumber(getAttr(root, 'data-pcmp-desktop-columns'), 4),
      mobileColumns: parseNumber(getAttr(root, 'data-pcmp-mobile-columns'), 1),
      desktopGap: parseNumber(getAttr(root, 'data-pcmp-desktop-gap'), 8),
      mobileGap: parseNumber(getAttr(root, 'data-pcmp-mobile-gap'), 8),
      desktopPeek: parseNumber(getAttr(root, 'data-pcmp-desktop-peek'), 0),
      mobilePeek: parseNumber(getAttr(root, 'data-pcmp-mobile-peek'), 0),

      sliderAutoplay: parseBoolean(getAttr(root, 'data-pcmp-slider-autoplay'), true),
      sliderLoop: parseBoolean(getAttr(root, 'data-pcmp-slider-loop'), true),
      sliderDirection: parseString(getAttr(root, 'data-pcmp-slider-direction'), 'rtl'),
      sliderAutoplayInterval: parseNumber(
        getAttr(root, 'data-pcmp-slider-autoplay-interval'),
        2500
      ),
      sliderSpeedDesktop: parseNumber(
        getAttr(root, 'data-pcmp-slider-speed-desktop'),
        560
      ),
      sliderSpeedMobile: parseNumber(
        getAttr(root, 'data-pcmp-slider-speed-mobile'),
        540
      ),
      pauseOnHoverDesktop: parseBoolean(
        getAttr(root, 'data-pcmp-pause-hover-desktop'),
        true
      ),
      respectReducedMotion: parseBoolean(
        getAttr(root, 'data-pcmp-respect-reduced'),
        false
      ),

      imageAutoplay: parseBoolean(getAttr(root, 'data-pcmp-image-autoplay'), false),
      imageAutoplayInterval: parseNumber(
        getAttr(root, 'data-pcmp-image-autoplay-interval'),
        1600
      ),
      hoverSecondary: parseBoolean(getAttr(root, 'data-pcmp-hover-secondary'), true),

      showArrowsDesktop: parseBoolean(
        getAttr(root, 'data-pcmp-show-arrows-desktop'),
        true
      ),
      showArrowsMobile: parseBoolean(
        getAttr(root, 'data-pcmp-show-arrows-mobile'),
        true
      ),

      adaptToImage: parseBoolean(getAttr(root, 'data-pcmp-adapt-to-image'), false),
      imageRatio: parseString(getAttr(root, 'data-pcmp-image-ratio'), '4_5'),

      dragDesktop: parseBoolean(getAttr(root, 'data-pcmp-drag-desktop'), false),
      dragMobile: parseBoolean(getAttr(root, 'data-pcmp-drag-mobile'), true),

      showComparePrice: parseBoolean(
        getAttr(root, 'data-pcmp-show-compare-price'),
        true
      ),
      showDiscountBadge: parseBoolean(
        getAttr(root, 'data-pcmp-show-discount-badge'),
        true
      ),
      showCurrencyCode: parseBoolean(
        getAttr(root, 'data-pcmp-show-currency-code'),
        true
      ),
    };

    return {
      root,
      sectionId,
      anchorId,

      elements: {
        slider,
        viewport,
        track,
        controls,
        prevButton,
        nextButton,
        slides,
        cards,
        mediaImages,
      },

      settings,

      state: {
        isDesktop: matchesDesktop(),
        prefersReducedMotion: prefersReducedMotion(),
        documentVisible: document.visibilityState !== 'hidden',
        sectionVisible: true,
      },
    };
  };


  /* ==========================================================================
     04. MODULE ADAPTERS
     ========================================================================== */

  const createModuleInstance = (moduleName, context) => {
    const ModuleClass = resolveModule(moduleName);
    if (!ModuleClass) return null;

    try {
      return new ModuleClass(context);
    } catch (_) {
      return null;
    }
  };

  const refreshModuleInstance = (instance, context) => {
    if (!instance) return;

    if (typeof instance.refresh === 'function') {
      safeCall(instance.refresh.bind(instance), context);
      return;
    }

    if (typeof instance.update === 'function') {
      safeCall(instance.update.bind(instance), context);
    }
  };

  const destroyModuleInstance = (instance) => {
    if (!instance) return;
    safeCall(instance.destroy?.bind(instance));
  };

  const setModuleSectionVisibility = (instance, isVisible) => {
    if (!instance) return;
    safeCall(instance.setSectionVisible?.bind(instance), isVisible);
  };

  const setModuleDocumentVisibility = (instance, isVisible) => {
    if (!instance) return;
    safeCall(instance.setDocumentVisible?.bind(instance), isVisible);
  };

  const setModuleEditorSelection = (instance, isSelected) => {
    if (!instance) return;

    if (isSelected) {
      safeCall(instance.onEditorSelect?.bind(instance));
    } else {
      safeCall(instance.onEditorDeselect?.bind(instance));
    }
  };


  /* ==========================================================================
     05. SECTION CONTROLLER
     ========================================================================== */

  class PCMPSectionController {
    constructor(root) {
      this.root = root;
      this.context = buildSectionContext(root);

      this.sliderInstance = null;
      this.cardMediaInstance = null;

      this.isMounted = false;
      this.isDestroyed = false;
      this.sectionVisible = true;
      this.documentVisible = document.visibilityState !== 'hidden';

      this.resizeObserver = null;
      this.intersectionObserver = null;

      this.onResize = rafThrottle(this.onResize.bind(this));
      this.onDocumentVisibilityChange = this.onDocumentVisibilityChange.bind(this);
      this.onShopifySectionSelect = this.onShopifySectionSelect.bind(this);
      this.onShopifySectionDeselect = this.onShopifySectionDeselect.bind(this);
    }

    mount() {
      if (this.isMounted || this.isDestroyed) return;

      this.refreshContext();
      this.mountModules();
      this.bindObservers();
      this.bindEvents();

      this.root.setAttribute('data-pcmp-ready', 'true');
      this.isMounted = true;

      dispatchSectionEvent(this.root, 'mounted', {
        sectionId: this.context.sectionId,
        anchorId: this.context.anchorId,
      });
    }

    refreshContext() {
      this.context = buildSectionContext(this.root);
      this.context.state.sectionVisible = this.sectionVisible;
      this.context.state.documentVisible = this.documentVisible;
    }

    mountModules() {
      this.sliderInstance = createModuleInstance('slider', this.context);
      this.cardMediaInstance = createModuleInstance('cardMedia', this.context);

      safeCall(this.sliderInstance?.mount?.bind(this.sliderInstance));
      safeCall(this.cardMediaInstance?.mount?.bind(this.cardMediaInstance));

      setModuleSectionVisibility(this.sliderInstance, this.sectionVisible);
      setModuleSectionVisibility(this.cardMediaInstance, this.sectionVisible);

      setModuleDocumentVisibility(this.sliderInstance, this.documentVisible);
      setModuleDocumentVisibility(this.cardMediaInstance, this.documentVisible);
    }

    bindObservers() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.onResize();
        });
        this.resizeObserver.observe(this.root);
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const isVisible =
              !!entry &&
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.15;

            this.sectionVisible = isVisible;
            this.refreshContext();

            setModuleSectionVisibility(this.sliderInstance, isVisible);
            setModuleSectionVisibility(this.cardMediaInstance, isVisible);

            dispatchSectionEvent(this.root, 'section-visibility', {
              sectionId: this.context.sectionId,
              isVisible,
            });
          },
          {
            threshold: [0, 0.15, 0.5, 1],
          }
        );

        this.intersectionObserver.observe(this.root);
      }
    }

    bindEvents() {
      document.addEventListener('visibilitychange', this.onDocumentVisibilityChange);

      document.addEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this.onShopifySectionDeselect);

      window.addEventListener('resize', this.onResize, { passive: true });
    }

    onResize() {
      if (this.isDestroyed) return;

      const previousDesktop = this.context?.state?.isDesktop;
      this.refreshContext();

      refreshModuleInstance(this.sliderInstance, this.context);
      refreshModuleInstance(this.cardMediaInstance, this.context);

      dispatchSectionEvent(this.root, 'resize', {
        sectionId: this.context.sectionId,
        isDesktop: this.context.state.isDesktop,
        breakpointChanged: previousDesktop !== this.context.state.isDesktop,
      });
    }

    onDocumentVisibilityChange() {
      if (this.isDestroyed) return;

      this.documentVisible = document.visibilityState !== 'hidden';
      this.refreshContext();

      setModuleDocumentVisibility(this.sliderInstance, this.documentVisible);
      setModuleDocumentVisibility(this.cardMediaInstance, this.documentVisible);

      dispatchSectionEvent(this.root, 'document-visibility', {
        sectionId: this.context.sectionId,
        isVisible: this.documentVisible,
      });
    }

    onShopifySectionSelect(event) {
      if (!event.target || event.target !== this.root.closest('.shopify-section')) return;

      setModuleEditorSelection(this.sliderInstance, true);
      setModuleEditorSelection(this.cardMediaInstance, true);

      dispatchSectionEvent(this.root, 'editor-select', {
        sectionId: this.context.sectionId,
      });
    }

    onShopifySectionDeselect(event) {
      if (!event.target || event.target !== this.root.closest('.shopify-section')) return;

      setModuleEditorSelection(this.sliderInstance, false);
      setModuleEditorSelection(this.cardMediaInstance, false);

      dispatchSectionEvent(this.root, 'editor-deselect', {
        sectionId: this.context.sectionId,
      });
    }

    destroy() {
      if (this.isDestroyed) return;

      this.isDestroyed = true;
      this.isMounted = false;

      document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange);
      document.removeEventListener('shopify:section:select', this.onShopifySectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onShopifySectionDeselect);
      window.removeEventListener('resize', this.onResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }

      destroyModuleInstance(this.sliderInstance);
      destroyModuleInstance(this.cardMediaInstance);

      this.sliderInstance = null;
      this.cardMediaInstance = null;

      this.root.removeAttribute('data-pcmp-ready');

      dispatchSectionEvent(this.root, 'destroyed', {
        sectionId: this.context.sectionId,
      });
    }
  }


  /* ==========================================================================
     06. MOUNT / UNMOUNT HELPERS
     ========================================================================== */

  const mountRoot = (root) => {
    if (!root || REGISTRY.has(root)) return;

    const controller = new PCMPSectionController(root);
    controller.mount();
    REGISTRY.set(root, controller);
  };

  const unmountRoot = (root) => {
    if (!root) return;

    const controller = REGISTRY.get(root);
    if (!controller) return;

    controller.destroy();
    REGISTRY.delete(root);
  };

  const mountAll = (scope = document) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(mountRoot);
  };

  const unmountAll = (scope = document) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(unmountRoot);
  };


  /* ==========================================================================
     07. SHOPIFY SECTION LIFECYCLE
     ========================================================================== */

  const onSectionLoad = (event) => {
    if (!event.target) return;
    mountAll(event.target);
  };

  const onSectionUnload = (event) => {
    if (!event.target) return;
    unmountAll(event.target);
  };


  /* ==========================================================================
     08. PUBLIC API
     ========================================================================== */

  window.PCMPMotionPro = window.PCMPMotionPro || {
    mount: mountAll,
    unmount: unmountAll,
    registry: REGISTRY,
    selectors: {
      root: ROOT_SELECTOR,
    },
    modules: MODULE_KEYS,
  };


  /* ==========================================================================
     09. INITIAL BOOT
     ========================================================================== */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        mountAll(document);
      },
      { once: true }
    );
  } else {
    mountAll(document);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
})();